import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  Resolve,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
  RedirectCommand
} from '@angular/router';
import { PanelService } from '../api/panel.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LocalStorageService } from '../storage/local-storage.service';
import { AppStateService } from '../app-state.service';
import { LocalStorageVariables } from '../../enum/localstorage-variables';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class StoreGroupResolverResolver implements Resolve<any> {
  private panelService = inject(PanelService);
  private localStorageService = inject(LocalStorageService);
  private appStateService = inject(AppStateService);
  private platformId = inject(PLATFORM_ID);

  storeGroups: any;
  private readonly CACHE_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

  constructor() {
    // Load cached data on initialization (SSR-safe)
    this.loadCachedData();
    // Load from AppState if available
    const cachedStoreGroups = this.appStateService.getStoreGroups();
    if (cachedStoreGroups) {
      this.storeGroups = cachedStoreGroups;
    }
  }

  private loadCachedData(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const cached = this.localStorageService.getItem(LocalStorageVariables.store_groups);
    if (cached && cached.data && cached.timestamp) {
      const now = Date.now();
      // Check if cache is still valid (not expired)
      if (now - cached.timestamp < this.CACHE_EXPIRY_MS) {
        this.storeGroups = cached.data;
      } else {
        // Cache expired, remove it
        this.localStorageService.deleteItem(LocalStorageVariables.store_groups);
      }
    }
  }

  private saveToCache(data: any): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.localStorageService.setItem(LocalStorageVariables.store_groups, {
      data: data,
      timestamp: Date.now()
    });
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any | RedirectCommand> {
    // Skip HTTP requests during SSR - return immediately
    if (isPlatformServer(this.platformId)) {
      return new Observable((subscriber) => {
        subscriber.next(null);
        subscriber.complete();
      });
    }
    
    // Check AppState first
    const cachedStoreGroups = this.appStateService.getStoreGroups();
    if (cachedStoreGroups) {
      this.storeGroups = cachedStoreGroups;
      return new Observable((subscriber) => {
        subscriber.next(this.storeGroups);
        subscriber.complete();
      });
    }
    // If store groups are already loaded, return immediately (non-blocking)
    if (this.storeGroups) {
      return new Observable((subscriber) => {
        subscriber.next(this.storeGroups);
        subscriber.complete();
      });
    }
    // Otherwise, fetch the data
    return this.panelService.getStoreGroups().pipe(
      tap((data) => {
        // Update AppState
        this.appStateService.setStoreGroups(data);
        this.storeGroups = data;
        this.saveToCache(data);
      })
    );
  }

  get permissionInit() {
    return this.storeGroups;
  }

  public updateInfo(): Observable<any> {
    return this.panelService.getStoreGroups().pipe(
      tap((data) => {
        // Update AppState
        this.appStateService.setStoreGroups(data);
        this.storeGroups = data;
        this.saveToCache(data);
      })
    );
  }
}
